import React from 'react';
import { Button } from '../../components/Button';
import { Select } from '../../components/Select';
import type { CajaData, CajaOpcion } from './types';

interface CajaDropdownProps {
  isOpen: boolean;
  data: CajaData;
  cajasDisponibles?: CajaOpcion[];
  cajaSeleccionadaId?: string;
  onCambiarCaja?: (cajaId: string) => void;
  onVerMovimientos: () => void;
  onCerrarCaja: () => void;
  onAbrirCaja: () => void;
}

export const CajaDropdown: React.FC<CajaDropdownProps> = ({
  isOpen,
  data,
  cajasDisponibles,
  cajaSeleccionadaId,
  onCambiarCaja,
  onVerMovimientos: _onVerMovimientos,
  onCerrarCaja,
  onAbrirCaja,
}) => {
  if (!isOpen) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  const selectorCaja = cajasDisponibles && cajasDisponibles.length > 1 && (
    <Select
      id="caja-status-selector"
      label="Caja"
      size="small"
      value={cajaSeleccionadaId ?? ''}
      onChange={(e) => onCambiarCaja?.(e.target.value)}
      options={cajasDisponibles.map(c => ({ value: c.id, label: c.nombre }))}
    />
  );

  if (!data.abierta) {
    return (
      <div
        className="absolute top-full right-0 mt-2 w-[280px] bg-surface-0 border border-default rounded-xl z-50"
        style={{
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
      >
        <div className="px-4 py-4 flex flex-col gap-3">
          {selectorCaja}
          <div>
            <p className="text-ui-base font-semibold text-primary">Caja Cerrada</p>
            <p className="text-ui-sm text-tertiary mt-0.5">La caja está cerrada. Ábrela para comenzar a operar.</p>
          </div>
          <Button
            variant="primary"
            size="md"
            onClick={onAbrirCaja}
            icon={
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 0110 0v4"/>
              </svg>
            }
            className="w-full"
          >
            Abrir Caja
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="absolute top-full right-0 mt-2 w-[320px] bg-surface-0 border border-secondary rounded-xl z-50"
      style={{
        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-muted bg-surface-0 rounded-t-xl">
        <div className="text-ui-base font-semibold text-primary mb-1">
          Caja #{data.numero} - Abierta
        </div>
        <div className="text-xs text-tertiary">
          Apertura: Hoy {data.apertura.hora} · {data.apertura.usuario}
        </div>
      </div>

      {selectorCaja && (
        <div className="px-4 py-3 border-b border-muted">
          {selectorCaja}
        </div>
      )}

      {/* Resumen del día */}
      <div className="px-4 py-3 border-b border-muted space-y-2">
        <div className="text-xs font-semibold text-tertiary uppercase tracking-wide mb-1">Resumen del día</div>
        <div className="flex items-center justify-between py-1">
          <span className="text-xs text-secondary font-medium">Monto apertura</span>
          <span className="text-sm font-semibold text-primary">{formatCurrency(data.apertura.monto)}</span>
        </div>
        <div className="flex items-center justify-between py-1">
          <span className="text-xs text-secondary font-medium">Efectivo ingresado</span>
          <span className="text-sm font-semibold text-primary">{formatCurrency(data.ingresos)}</span>
        </div>
        <div className="flex items-center justify-between py-1.5 mt-1 bg-surface-1 rounded-lg px-2 -mx-2">
          <span className="text-sm font-semibold text-primary">Efectivo en caja</span>
          <span className="text-base font-bold text-success">{formatCurrency(data.saldo)}</span>
        </div>
      </div>

      {/* Footer con acciones */}
      <div className="px-3 py-3 border-t border-muted bg-surface-0 rounded-b-xl flex gap-2">
        <Button
          variant="primary"
          size="md"
          onClick={onCerrarCaja}
          icon={
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
          }
          iconPosition="left"
          className="flex-1 text-sm"
        >
          Cerrar Caja
        </Button>
      </div>
    </div>
  );
};


